/**
 * Handles a bubble-phase click that may originate from a code-block copy control.
 *
 * @param event - DOM click event (typically from `document` delegation).
 */
export function handleCopyCodeToClipboardClick(event: Event): void {
  const target = event.target;
  if (!target || !(target instanceof Element)) {
    return;
  }

  const copyIcon = target.closest('.copy-icon');
  if (!copyIcon) {
    return;
  }

  const pre = copyIcon.closest('pre');
  if (!pre) {
    return;
  }

  const copySource = pre.querySelector<HTMLTextAreaElement>(
    'textarea.copy-source',
  );
  const copiedIcon = pre.querySelector('.copied-icon');

  if (!copySource?.value) {
    return;
  }

  const { clipboard } = navigator;
  if (!clipboard?.writeText) {
    return;
  }

  void clipboard
    .writeText(copySource.value)
    .then(() => {
      copyIcon.classList.add('copied');
      copiedIcon?.classList.add('copied');
      setTimeout(() => {
        copyIcon.classList.remove('copied');
        copiedIcon?.classList.remove('copied');
      }, 2000);
    })
    .catch(() => {
      /* copy unavailable, insecure context, or permission denied */
    });
}

/**
 * Registers document-level delegation for code-block copy. Returns an unsubscribe
 * function so tests (or embedders) can remove the listener.
 */
export function registerCopyCodeToClipboard(): () => void {
  const listener: EventListener = (event) => {
    handleCopyCodeToClipboardClick(event);
  };
  document.addEventListener('click', listener);
  return () => document.removeEventListener('click', listener);
}
